import { BuildingType } from "../config/buildings";
import { ProductType } from "../config/products";
import { QuickLinks } from "../config/quicklinks";
import { TechSector } from "../config/tech-sector";
import { Language } from '../interfaces/language';

import solutionsImg from "../assets/buildings/quicklinks/laptop.webp"
import knowHowImg from "../assets/buildings/quicklinks/books.webp"
import informationImg from "../assets/buildings/quicklinks/folder.webp"
import toolsImg from "../assets/buildings/quicklinks/tools.webp"
import newsImg from "../assets/buildings/quicklinks/microphone.webp"
import careerImg from "../assets/buildings/quicklinks/arrows.webp"

const productCatalogText = 'Zum Produktkatalog';
const detailpageText = 'Zur Detailseite';
const planingText = 'Zur Auslegung';

const SHY = '\u00AD'; // soft hyphen for line breaks in long words

const defaultPlanningLink = (linksToPlaning?: string) => ({
        text: planingText,
        href: linksToPlaning ?? 'https://www.ksb.com/de-de/software-und-know-how/auslegungstools-und-hilfen/ksbuilding-consult',
});

const defaultDetailpageLink = (
    linksToDetailpage?: string,
    sector?: 'App' | 'hebeanlagen' | 'druckerhoehung' | 'tauchmotor' | 'nasslaeufer',
) => {
    const getDetailpageLink = (sector: 'App' | 'hebeanlagen' | 'druckerhoehung' | 'tauchmotor' | 'nasslaeufer' | '') => {
        switch (sector) {
            case 'App':
                return 'https://www.ksbuilding-consult.com/ApplRange.aspx';
            case 'hebeanlagen':
                return 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/entwaesserung/hebeanlagen';
            case 'druckerhoehung':
                return 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/produkte/druckerhoehungsanlagen';
            case 'tauchmotor':
                return 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/entwaesserung/tauchmotorpumpen';
            case 'nasslaeufer':
                return 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/waerme-und-kaelteversorgung/nasslaeuferpumpen';
            default:
                return 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/produkte/strangregulierventile';
        }
    };
    return {
        text: detailpageText,
        href: linksToDetailpage ?? getDetailpageLink(sector ?? ''),
    };
};

const BrochureWasteWaterLink =
    {
        text: 'Zur Know-how-Broschüre',
        href: 'https://go.ksb.com/know-how-broschuere-entwaesserung.html',
    };

const DigitalDataLinks =
{
    text: 'Digitale Daten',
    href: 'https://www.ksb.com/de-de/digitale-produktdaten',
};

const heatingDetailpageLink = {

};

export const de: Language = {
    locale: { language: 'de' },
    appTitle: 'Herzlich Willkommen in der KSBuilding World: Pumpen und Armaturen für die Gebäudetechnik',
    appSubTitle: 'Entdecken Sie das gebäudetechnische Angebot von KSB interaktiv in der KSBuilding City',
    backToCity: 'zurück zur KSBuilding City',
    back: 'zurück',
    city: {
        comingSoonBuilding: 'Richtfest in Kürze',
        comingSoonTechSector: 'Coming soon',
        newBuilding: 'Neu',
    },
    buildingDetails: {
        navigationTitle: 'Wählen Sie einen Anwendungsbereich',
        navigationDescription:
            'Wählen Sie ein Produkt aus und erfahren Sie alles rund um die Anwendung und die Auslegung.',
        new: 'Neu',
    },
    newsletter: {
        title: 'Keine Neuheiten in der KSBuilding City verpassen?',
        link: {
            text: 'Hier zum Newsletter anmelden!',
            href: 'https://go.ksb.com/de-de-ksbuilding-newsletter',
        },
    },
    contact: {
        text: 'Kontakt',
        href: 'https://www.ksb.com/de-de/kontakt',
    },
    orientationPopup: {
        title: 'Hinweis',
        message: 'Bitte ins Querformat drehen, um alle Funktionen optimal zu nutzen.',
        closeButton: 'Verstanden',
    },
    buildings: {
        [BuildingType.HOUSE]: {
            name: 'Einfamilienhaus',
        },
        [BuildingType.APARTMENT]: {
            name: 'Mehrfamilienhaus',
        },
        [BuildingType.HOSPITAL]: {
            name: 'Krankenhaus',
        },
        [BuildingType.SCHOOL]: {
            name: 'Schulgebäude',
        },
        [BuildingType.ADMINISTRATION]: {
            name: 'Verwaltung',
        },
        [BuildingType.SKYSCRAPER]: {
            name: 'Hochhaus',
        },
        [BuildingType.INDUSTRY]: {
            name: 'Industrie',
        },
        [BuildingType.INDUSTRY_LAB]: {
            name: 'Labor',
            mainBuilding: {
                building: BuildingType.INDUSTRY,
                sector: TechSector.WATER,
            },
        },
        [BuildingType.INDUSTRY_PRODUCTION]: {
            name: 'Produktionsgebäude',
            mainBuilding: {
                building: BuildingType.INDUSTRY,
                sector: TechSector.WATER,
            },
        },
        [BuildingType.INDUSTRY_TESTCENTER]: {
            name: 'Prüfstand',
        },
        [BuildingType.INDUSTRY_SPRINKLER_CENTER]: {
            name: 'Sprinklerzentrale',
            mainBuilding: {
                building: BuildingType.INDUSTRY,
                sector: TechSector.FIRE_PROTECTION,
            },
        },
        [BuildingType.INDUSTRY_WATER]: {
            name: 'Zentrale Wasserversorgung',
            mainBuilding: {
                building: BuildingType.INDUSTRY,
                sector: TechSector.WATER,
            },
        },
        [BuildingType.FACTORY]: {
            name: 'Fabrikgelände',
        },
        [BuildingType.FACTORY_HEATING]: {
            name: 'Heizzentrale',
            mainBuilding: {
                building: BuildingType.FACTORY,
                sector: TechSector.HEATING,
            },
        },
        [BuildingType.FACTORY_SPRINKLER_CENTER]: {
            name: 'Sprinklerzentrale',
            mainBuilding: {
                building: BuildingType.FACTORY,
                sector: TechSector.FIRE_PROTECTION,
            },
        },
        [BuildingType.FACTORY_PROCESSES]: {
            name: 'Industrielle Prozesse',
            mainBuilding: {
                building: BuildingType.FACTORY,
                sector: TechSector.WATER,
            },
        },
        [BuildingType.FACTORY_PRODUCTION]: {
            name: 'Produktionsgebäude',
            mainBuilding: {
                building: BuildingType.FACTORY,
                sector: TechSector.WATER,
            },
        },
        [BuildingType.HOTEL]: {
            name: 'Hotel',
        },
        [BuildingType.INDUSTRY_HEATING]: {
            name: 'Produktionsgebäude',
            mainBuilding: {
                building: BuildingType.INDUSTRY,
                sector: TechSector.HEATING,
            },
        },
        [BuildingType.SKYSCRAPER_WATER_UPPER]: {
            name: 'Druckzone 2',
            mainBuilding: {
                building: BuildingType.SKYSCRAPER,
                sector: TechSector.WATER,
            },
            navText: 'Zur Druckzone 2',
        },
        [BuildingType.SKYSCRAPER_WATER_LOWER]: {
            name: 'Druckzone 1',
            mainBuilding: {
                building: BuildingType.SKYSCRAPER,
                sector: TechSector.WATER,
            },
            navText: 'Zur Druckzone 1',
        },
        [BuildingType.DATA_CENTER]: {
            name: 'Rechenzentrum',
        },
        [BuildingType.DATA_CENTER_COOLING_AIR]: {
            name: 'Air cooling',
            mainBuilding: {
                building: BuildingType.DATA_CENTER,
                sector: TechSector.HEATING,
            },
        },
        [BuildingType.DATA_CENTER_COOLING_DIRECT]: {
            name: 'Direct-to-chip liquid cooling',
            mainBuilding: {
                building: BuildingType.DATA_CENTER,
                sector: TechSector.HEATING,
            },
        },
        [BuildingType.DATA_CENTER_COOLING_LIQUID]: {
            name: 'Liquid cooling',
            mainBuilding: {
                building: BuildingType.DATA_CENTER,
                sector: TechSector.HEATING,
            },
        },
        [BuildingType.KSB_QUICKLINKS]: {
            name: 'Quicklinks',
            quicklinks: {
                [QuickLinks.SOLUTIONS]: {
                    name: 'Lösungen für die Planung',
                    color: '#a12b95',
                    img: solutionsImg,
                    href: 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/smarte-planungshilfen',
                },
                [QuickLinks.KNOW_HOW]: {
                    name: 'Know-How',
                    color: '#53951f',
                    img: knowHowImg,
                    sublinks: [
                        {
                            name: 'Online-Seminare',
                            href: 'https://www.ksb.com/de-de/know-how/online-seminare-on-line-mit-ksb',
                        },
                        {
                            name: 'Tutorials',
                            href: 'https://www.ksb.com/de-de/know-how/wissenshub?contentsearch.facet.format=%5B%222019044%22%5D',
                        },
                        {
                            name: 'Know-how-Broschüren',
                            href: 'https://www.ksb.com/de-de/know-how/wissenshub?contentsearch.facet.format=%5B%222040244%22%5D',
                        },
                        {
                            name: 'Kreisel\u00ADpumpen\u00ADlexikon',
                            href: 'https://www.ksb.com/de-global/kreiselpumpenlexikon',
                        },
                    ],
                },
                [QuickLinks.INFORMATION]: {
                    name: 'Produkt\u00ADinformationen',
                    color: '#ff9200',
                    img: informationImg,
                    sublinks: [
                        {
                            name: 'Produkt\u00ADkatalog',
                            href: 'https://www.ksb.com/de-de/produkte/produktkatalog',
                        },
                        {
                            name: 'E-Paper-Portal',
                            href: 'https://oxomi.com/p/2024617/switch?oxDeeplink=eyJ1bml2ZXJzYWxTZWFyY2h8MyI6eyJ2aWV3IjoiaG9tZSJ9fQ%3D%3D',
                        },
                    ],
                },
                [QuickLinks.TOOLS]: {
                    name: 'Tools und Support für die Praxis',
                    color: '#a4231d',
                    img: toolsImg,
                    sublinks: [
                        {
                            name: 'Tools für Installateure',
                            href: 'https://www.ksb.com/de-de/know-how/wissenshub?contentsearch.facet.goal=%5B%222037748%22%2C%222037750%22%2C%222037752%22%5D&contentsearch.facet.application=%5B%221269342%22%5D',
                        },
                        {
                            name: 'Autorisierte Service-Partner',
                            href: 'https://www.ksb.com/de-de/supremeserv/ksb-supremeserv-deutschland/autorisierte-service-partner#!',
                        },
                    ],
                },
                [QuickLinks.NEWS]: {
                    name: 'News',
                    color: '#009791',
                    img: newsImg,
                    sublinks: [
                        {
                            name: 'Newsletter',
                            href: 'https://go.ksb.com/de-de-ksbuilding-newsletter',
                        },
                        {
                            name: 'PR',
                            href: 'https://www.ksb.com/de-de/ueber-ksb/pressebereich',
                        },
                    ],
                },
                [QuickLinks.CAREER]: {
                    name: 'Karriere',
                    color: '#ccd800',
                    img: careerImg,
                    href: 'https://www.ksb.com/de-de/ueber-ksb/karriere',
                },
            },
        },
        [BuildingType.KSB_TEAM]: {
            name: '',
            ctaText: '',
            members: {
                beck: {
                    title: '',
                    href: '',
                    content: '',
                },
                bollmann: {
                    title: '',
                    href: '',
                    content: '',
                },
                schmidt: {
                    title: '',
                    href: '',
                    content: '',
                },
                terock: {
                    title: '',
                    href: '',
                    content: '',
                },
                manss: {
                    title: '',
                    href: '',
                    content: '',
                },
            },
        },
        [BuildingType.KSB_CENTER]: {
            name: '',
            sectionTeam: '',
            sectionNews: '',

            headline: '',
            teaser: {
                title: '',
                text: '',
                footer: '',
            },
            teaserEtaline: {
                link: {
                    text: '',
                    href: '',
                },
            },
            linkList: [],
        },
    },
    sectors: {
        [TechSector.WATER]: {
            name: `Wasser${SHY}versorgung`,
        },
        [TechSector.HEATING]: {
            name: 'Wärme- / Kälteversorgung',
        },
        [TechSector.WASTE_WATER]: {
            name: 'Entwässerung',
        },
        [TechSector.FIRE_PROTECTION]: {
            name: 'Brandschutz',
        },
    },
    products: {
        [ProductType.AMA_DRAINER_3]: {
            name: 'AmaDrainer 3',
            links: [
                {
                    text: detailpageText,
                    href: 'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/produkte/amadrainer3',
                },
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Gebäudetechnik.Entwässerung.Schmutzwasser-Tauchmotorpumpen.Schmutzwasserpumpen.',
                ),
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/amadrainer-3/A07B',
                },
                {
                    text: 'Zur Know-how-Broschüre',
                    href: 'https://live-resources-e2e-sales.ksb.com/resource/blob/166112/fcac767dc8ef8b94919b12aed4fda6d9/dow-2drainage-planning-data.pdf',
                },
            ],
        },
        [ProductType.AMA_DRAINER_4_5]: {
            name: 'AmaDrainer 4/5',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/amadrainer-4-5-amadrainer-80-100/A76A',
                },
                defaultDetailpageLink(undefined, 'tauchmotor'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Schmutzwasser-Tauchmotorpumpen.Schmutzwasserpumpen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.AMA_DRAINER_BOX_UNTERFLUR]: {
            name: 'AmaDrainer Box Unterflur',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/amadrainer-box-amadrainer-box-mini/A23A',
                },
                defaultDetailpageLink(undefined, 'hebeanlagen'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Hebeanlagen.Schmutzwasserhebeanlagen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.AMA_DRAINER_BOX_UEBERFLUR]: {
            name: 'AmaDrainer Box Überflur',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/system/ama-drainer-box-ama-drainer-box-mini/A23A',
                },
                defaultDetailpageLink(undefined, 'hebeanlagen'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Hebeanlagen.Schmutzwasserhebeanlagen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.AMAPORTER]: {
            name: 'AmaPorter',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/tauchmotorpumpe/ama-porter/A10A',
                },
                defaultDetailpageLink(undefined, 'tauchmotor'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Schmutzwasser-Tauchmotorpumpen.Schmutzwasserpumpen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.AMAREX]: {
            name: 'Amarex',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/amarex/A31B',
                },
                defaultDetailpageLink(undefined, 'tauchmotor'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Schmutzwasser-Tauchmotorpumpen.Tauchmotorpumpen%20(mit/%20ohne%20Ex%20Schutz).',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.BOA_COMPACT_EKB]: {
            name: 'BOA-Compact EKB',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/Ventil/c/PLUG_VLV/B02A',
                },
            ],
        },
        [ProductType.BOA_CONTROL_DPR]: {
            name: 'BOA-Control DPR',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/ventil/boa-control-dpr/B66A',
                },
                defaultDetailpageLink(),
                DigitalDataLinks,
            ],
        },
        [ProductType.BOA_CONTROL_IMS]: {
            name: 'BOA‑Control IMS',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/ventil/boa-control-boa-control-ims/B05B',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.BOA_CONTROL_PIC]: {
            name: 'BOA-Control PIC',
            links: [
                defaultDetailpageLink(),
                defaultPlanningLink(),
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/ventil/boa-control-pic/B75A',
                },
            ],
        },
        [ProductType.BOA_CONTROL_SBV]: {
            name: 'BOA-Control SBV',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/ventil/boa-control-sbv/B79A',
                },
                defaultDetailpageLink(),
                DigitalDataLinks,
            ],
        },
        [ProductType.BOA_H]: {
            name: 'BOA-H',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/ventil/boa-h/B08A',
                },
            ],
        },
        [ProductType.BOA_RVK]: {
            name: 'BOA-RVK',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/R%25C3%25BCckschlagventil/c/CHCK_VLV/B11A',
                },
            ],
        },
        [ProductType.BOA_SYSTRONIC_EPIC]: {
            name: 'BOA-Systronic ePIC',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/boa-systronic-epic/B95A',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.BOATRONIC_MP]: {
            name: 'BOATRONIC MP',
            links: [defaultDetailpageLink()],
        },
        [ProductType.BOAX_MAT]: {
            name: 'BOAXMAT',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/klappe/boax-s-sf/B12A',
                },
            ],
        },
        [ProductType.BOAX_S_SF]: {
            name: 'BOAX-S/SF',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/klappe/boax-s-sf/B12A',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.CALIO_PRO_PLUS]: {
            name: 'Calio Pro Plus',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/calio-pro-plus/C24A',
                },
                defaultDetailpageLink(undefined, 'nasslaeufer'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Na%C3%9Fl%C3%A4ufer%20Umw%C3%A4lzpumpen.Einzelpumpen.',
                ),
            ],
        },
        [ProductType.CALIO_PRO_PLUS_Z]: {
            name: 'Calio Pro Plus Z',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/C25A',
                },
                defaultDetailpageLink(undefined, 'nasslaeufer'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Na%C3%9Fl%C3%A4ufer%20Umw%C3%A4lzpumpen.Zwillingspumpe.',
                ),
            ],
        },
        [ProductType.CALIO_S_PRO]: {
            name: 'Calio S Pro',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/calio-s-pro/C90C',
                },
                defaultDetailpageLink(undefined, 'nasslaeufer'),
                defaultPlanningLink('https://www.ksbuilding-consult.com/ApplRange.aspx'),
            ],
        },
        [ProductType.CALIO_THERM_S_PRO]: {
            name: 'CalioTherm S Pro',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/caliotherm-s-pro/C91C',
                },
                defaultDetailpageLink(undefined, 'nasslaeufer'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Na%C3%9Fl%C3%A4ufer%20Umw%C3%A4lzpumpen.Einzelpumpen.',
                ),
            ],
        },
        [ProductType.CK_800]: {
            name: 'Pumpstation CK 800',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/system/pumpstation-ck-1000/C05A',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/entwaesserung/abwasserpumpstationen',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Pumpstationen%20(mit%20Ex%20Schutz).',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.COMPACTA]: {
            name: 'Compacta',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/system/compacta/C00B',
                },
                defaultDetailpageLink(undefined, 'hebeanlagen'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Hebeanlagen.F%C3%A4kalienhebeanlagen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.DELTA_COMPACT]: {
            name: 'DeltaCompact',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/ksb-delta-compact/D05B',
                },
                defaultDetailpageLink(undefined, 'druckerhoehung'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Druckerh%C3%B6hungsanlagen.Mehrpumpen%20Druckerh%C3%B6hungsanlagen.Drehzahlgeregelt.',
                ),
            ],
        },
        [ProductType.DELTA_MACRO]: {
            name: 'DeltaMacro',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/ksb-delta-macro/D12A',
                },
                defaultDetailpageLink(undefined, 'druckerhoehung'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Gebäudetechnik.Wasserversorgung.Druckerhöhungsanlagen.Mehrpumpen Druckerhöhungsanlagen.&AW__SERIES="DeltaMacro SVP","DeltaMacro VC","DeltaMacro F"',
                ),
            ],
        },
        [ProductType.DELTA_PRIMO_SVP]: {
            name: 'DeltaPrimo SVP',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/ksb-delta-primo/D08A',
                },
                defaultDetailpageLink(undefined, 'druckerhoehung'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Druckerh%C3%B6hungsanlagen.Mehrpumpen%20Druckerh%C3%B6hungsanlagen.',
                ),
            ],
        },
        [ProductType.ECOLINE_GT_40]: {
            name: 'ECOLINE GT 40',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/plattenschieber/ecoline-gt-40/EF2A',
                },
            ],
        },
        [ProductType.ETABLOC]: {
            name: 'Etabloc',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/global-search?search=etabloc',
                },
                defaultDetailpageLink('https://www.ksb.com/de-de/loesungen/industrie/produkte/eta'),
            ],
        },
        [ProductType.ETACHROM]: {
            name: 'Etachrom',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/E02A',
                },
                defaultDetailpageLink('https://www.ksb.com/de-de/loesungen/industrie/produkte/eta'),
            ],
        },
        [ProductType.ETALINE]: {
            name: 'Etaline',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/etaline/E03B',
                },
                defaultDetailpageLink('https://www.ksb.com/de-de/loesungen/industrie/produkte/eta'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Trockenl%C3%A4uferpumpen%20geregelt.Zwillingspumpe.',
                ),
            ],
        },
        [ProductType.ETALINE_SYT]: {
            name: 'Etaline SYT',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/etaline-syt/E12B',
                },
                defaultDetailpageLink('https://www.ksb.com/de-de/anwendungen/industrie/produkte/eta'),
            ],
        },
        [ProductType.ETALINE_Z]: {
            name: 'Etaline Z',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/E13B',
                },
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Trockenl%C3%A4uferpumpen%20geregelt.Zwillingspumpe.',
                ),
            ],
        },
        [ProductType.ETANORM]: {
            name: 'Etanorm',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/etanorm/E04B',
                },
                defaultDetailpageLink('https://www.ksb.com/de-de/loesungen/industrie/produkte/eta'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Heizung/Klima/K%C3%A4lte.Heizung.Trockenl%C3%A4uferpumpen%20geregelt.Einzelpumpen.',
                ),
            ],
        },
        [ProductType.ETANORM_FXV]: {
            name: 'Etanorm FXV',
            links: [
                defaultDetailpageLink('https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/brandschutz/sprinkler'),
            ],
        },
        [ProductType.ETASECO]: {
            name: 'EtaSeco',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/E07B',
                },
            ],
        },
        [ProductType.FILTRA]: {
            name: 'Filtra',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/filtra-n/F00A',
                },
                defaultPlanningLink('https://www.ksbuilding-consult.com/ApplRange.aspx'),
            ],
        },
        [ProductType.HPK]: {
            name: 'HPK',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/hpk/H02A',
                },
            ],
        },
        [ProductType.HYASOLO_2_D_FL_COMPACT]: {
            name: 'HyaSolo 2 D FL Compact',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/System/Druckerh%25C3%25B6hungsanlage/c/PRBOUN/H46B',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/brandschutz/hydrantenpumpen',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Brandschutz.Hydrantenanlagen.Doppelpumpen-Feuerl%C3%B6schanlagen.',
                ),
            ],
        },
        [ProductType.HYADUO_2_D_FL_COMPACT]: {
            name: 'HyaDuo 2 D FL Compact',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/System/Druckerh%25C3%25B6hungsanlage/c/PRBOUN/H46B',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/brandschutz/hydrantenpumpen',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Brandschutz.Hydrantenanlagen.Doppelpumpen-Feuerl%C3%B6schanlagen.',
                ),
            ],
        },
        [ProductType.HYA_RAIN_2_PLUS]: {
            name: 'HyaRain 2 Plus',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/hyarain-2-hyarain-2-plus/H12B',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/wasserversorgung/regenwassernutzung',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Regenwassernutzung.',
                ),
            ],
        },
        [ProductType.ISORIA_10_16]: {
            name: 'ISORIA 10/16',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/Klappe/c/BTTRFL_VLV/I00A',
                },
            ],
        },
        [ProductType.IXO_PRO]: {
            name: 'Ixo Pro',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/ixo-pro/I06A',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/wasserversorgung/bewaesserungspumpen',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Brunnen.',
                ),
            ],
        },
        [ProductType.KSB_SAFETY_BOOST]: {
            name: 'KSB Safety Boost ',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/System/c/SSTM/SA2A',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/wasserversorgung/regenwassernutzung',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Trennstationen.',
                ),
            ],
        },
        [ProductType.LEVELCONTROL_BASIC_2]: {
            name: 'LevelControl Basic 2',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpenautomatisierung/c/PUMP_ATMTN/L20A',
                },
                defaultPlanningLink(),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.MAGNOCHEM]: {
            name: 'Magnochem',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/M00B',
                },
            ],
        },
        [ProductType.MAGNOCHEM_BLOC]: {
            name: 'Magnochem-Bloc',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Trocken-aufgestellte-Pumpe/c/DRY/M08B',
                },
            ],
        },
        [ProductType.MEGA_CPK]: {
            name: 'MegaCPK',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/megacpk/M48A',
                },
            ],
        },
        [ProductType.MINI_COMPACTA]: {
            name: 'MiniCompacta',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/system/mini-compacta/M09B',
                },
                defaultDetailpageLink(undefined, 'hebeanlagen'),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Entw%C3%A4sserung.Hebeanlagen.F%C3%A4kalienhebeanlagen.',
                ),
                BrochureWasteWaterLink,
            ],
        },
        [ProductType.MOVITEC]: {
            name: 'Movitec',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/gliederpumpe/movitec/M12A',
                },
                defaultDetailpageLink(),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Gebäudetechnik.Heizung/Klima/Kälte.Heizung.&AW__SERIES="Movitec","Movitec PumpDrive"',
                ),
            ],
        },
        [ProductType.MOVITEC_VCI]: {
            name: 'Movitec VCI',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/wellentauchpumpe/movitec-vci/M94A',
                },
            ],
        },
        [ProductType.MULTI_ECO]: {
            name: 'MultiEco',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/pumpe/trocken-aufgestellte-pumpe/multieco/M17A',
                },
                defaultDetailpageLink(
                    'https://www.ksb.com/de-de/anwendungen/gebaeudetechnik/wasserversorgung/hauswasserversorgung',
                ),
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Hauswasserwerke.',
                ),
            ],
        },
        [ProductType.SERIE_2000]: {
            name: 'SERIE 2000',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/R%25C3%25BCckschlagklappe/c/SWNG_CHCK_VLV/S51A',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.SISTO_16_RGAMAXX]: {
            name: 'SISTO-16RGAMaXX',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/Membranventil/c/DPHRGM_VLV/S41B',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.SISTO_16_TWA]: {
            name: 'SISTO-16TWA',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Armatur/Membranventil/c/DPHRGM_VLV/S43A',
                },
            ],
        },
        [ProductType.SISTO_RSK]: {
            name: 'SISTO-RSK',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/produkte/armatur/rueckschlagklappe/sisto-rsk/S65A',
                },
                defaultDetailpageLink(),
            ],
        },
        [ProductType.UPA]: {
            name: 'UPA',
            links: [
                {
                    text: productCatalogText,
                    href: 'https://www.ksb.com/de-de/lc/Produkte/Pumpe/Unterwassermotorpumpe/c/UMP/U16A',
                },
                defaultPlanningLink(
                    'https://www.ksbuilding-consult.com/pumpdeeplink.aspx?Startoption=S4P_ApplRangeSelection&AW__APPLRG=KSB.Geb%C3%A4udetechnik.Wasserversorgung.Brunnen.  ',
                ),
            ],
        },
        // french products
        [ProductType.BOA_CVE_IMS]: undefined,
        [ProductType.BOATRONIC_MS_420]: undefined,
        [ProductType.HYA_RAIN_N]: undefined,
        [ProductType.DELTA_BASIC_COMPACT_MVP]: undefined,
    },
} as const;
